import { useEffect, useState } from 'react';
import api from '../services/api';
import { helpIdentity, storedHelpScope } from '../utils/helpScope';
import { captureSession, isSessionCurrent } from '../utils/authSession';

/** Counts are account-scoped on the server; never keep a previous account's badge. */
export default function useHelpUnread(user, sessionVersion) {
  const role = user?.role;
  const identity = helpIdentity(user, sessionVersion);
  const [value, setValue] = useState({ identity: '', count: 0 });
  useEffect(() => {
    if (
      user?.isActive === false ||
      user?.adminWorkspace === 'distributor' ||
      !['admin', 'sales_rep', 'clinic', 'master_distributor'].includes(role)
    )
      return undefined;
    let active = true;
    let timer;
    let running = false;
    let delay = 20000;
    const session = captureSession();
    const scope = storedHelpScope();
    const controller = new AbortController();
    const current = () => isSessionCurrent(session) && scope === storedHelpScope();
    const refresh = async () => {
      clearTimeout(timer);
      if (!active || running || !current() || document.visibilityState === 'hidden') return;
      running = true;
      try {
        const admin = role === 'admin';
        const response = await api.post(
          admin ? '/admin/support/requests/search' : '/support/requests/search',
          {
            status: 'all',
            search: '',
            cursor: null,
            limit: 1,
            ...(admin ? { assignment: 'all' } : {}),
          },
          { sessionBinding: session, signal: controller.signal }
        );
        const data = response.data?.data ?? response.data;
        if (active && current())
          setValue({ identity, count: Math.max(0, Number(data?.unreadCount) || 0) });
        delay = 20000;
      } catch (error) {
        if (active && current() && [401, 403, 404].includes(error.response?.status))
          setValue({ identity, count: 0 });
        delay = Math.min(delay * 2, 120000);
      } finally {
        running = false;
        if (active && current() && document.visibilityState !== 'hidden')
          timer = setTimeout(refresh, delay);
      }
    };
    const changed = (event) => {
      if (
        current() &&
        event.detail?.identity === identity &&
        Number.isFinite(event.detail?.count)
      ) {
        setValue({ identity, count: Math.max(0, event.detail.count) });
      }
    };
    const visibility = () => {
      if (document.visibilityState === 'hidden') clearTimeout(timer);
      else refresh();
    };
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('support:unread', changed);
    refresh();
    return () => {
      active = false;
      controller.abort();
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('support:unread', changed);
    };
  }, [identity, role, user?.isActive, user?.adminWorkspace]);
  return value.identity === identity ? value.count : 0;
}
